/* No dependencies. Real Shopify product forms keep purchases working without JS. */
(() => {
  if (!customElements.get('ge-airwrap')) {
    customElements.define('ge-airwrap', class extends HTMLElement {
      connectedCallback() {
        if (this.controller) return;
        this.controller = new AbortController();
        const { signal } = this.controller;
        this.addEventListener('click', (event) => {
          const button = event.target.closest('[data-ge-thumbnail]');
          if (!button) return;
          const id = button.dataset.geThumbnail;
          this.querySelectorAll('[data-ge-media]').forEach((panel) => {
            const selected = panel.dataset.geMedia === id;
            panel.hidden = !selected;
            if (!selected) {
              panel.querySelectorAll('video').forEach((video) => video.pause());
              panel.querySelectorAll('iframe').forEach((frame) => {
                // Reload only a previously selected external player to stop its audio.
                if (panel.dataset.geActive === 'true') frame.src = frame.src;
              });
            }
            panel.dataset.geActive = String(selected);
          });
          this.querySelectorAll('[data-ge-thumbnail]').forEach((thumb) => thumb.setAttribute('aria-pressed', String(thumb === button)));
        }, { signal });
        this.querySelectorAll('[data-ge-media]').forEach((panel) => { panel.dataset.geActive = String(!panel.hidden); });
        const picker = this.querySelector('[data-ge-variant]');
        picker?.addEventListener('change', () => {
          // Server-render the selected variant: price, inventory, quantity rules and
          // accelerated checkout all stay in sync, without theme-specific events.
          this.querySelectorAll('button[name="add"]').forEach((button) => { button.disabled = true; });
          const url = new URL(window.location.href);
          url.searchParams.set('variant', picker.value);
          url.hash = 'ge-product-top';
          window.location.assign(url.toString());
        }, { signal });
        const sticky = this.querySelector('[data-ge-sticky]');
        const form = this.querySelector('.ge-form');
        this.setupPersonalization(form, signal);
        if (sticky && form && 'IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(([entry]) => {
            sticky.hidden = entry.isIntersecting || entry.boundingClientRect.bottom > 0;
          }, { threshold: 0 });
          this.observer.observe(form);
        }
      }
      setupPersonalization(form, signal) {
        const offer = this.querySelector('[data-ge-personal]');
        if (!offer || !form) return;
        const toggle = offer.querySelector('[data-ge-personal-toggle]');
        const fields = offer.querySelector('[data-ge-personal-fields]');
        const input = offer.querySelector('[data-ge-personal-text]');
        const count = offer.querySelector('[data-ge-personal-count]');
        const express = this.querySelector('.ge-express');
        const error = this.querySelector('[data-ge-personal-error]');
        const cartLink = this.querySelector('[data-ge-personal-cart]');
        offer.hidden = false;
        const validate = () => {
          count.textContent = `${input.value.length} / 6`;
          input.setCustomValidity(toggle.checked && (!input.value.trim() || input.value.length > 6)
            ? 'Enter a name or initials of 1–6 characters.' : '');
        };
        const sync = () => {
          fields.hidden = !toggle.checked;
          input.disabled = !toggle.checked;
          input.required = toggle.checked;
          toggle.setAttribute('aria-expanded', String(toggle.checked));
          // Wallet shortcuts can bypass required text validation. Personalized
          // purchases use the validated Buy now flow; wallets remain in checkout.
          if (express) express.hidden = toggle.checked;
          validate();
        };
        toggle.addEventListener('change', () => { sync(); if (toggle.checked) input.focus(); }, { signal });
        input.addEventListener('input', validate, { signal });
        sync();
        form.addEventListener('submit', async (event) => {
          if (!toggle.checked) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          if (this.personalizationPending) return;
          validate();
          if (!form.reportValidity()) return;
          const data = new FormData(form);
          const id = Number(data.get('id'));
          const quantity = Number(data.get('quantity') || 1);
          const properties = {};
          for (const [key, value] of data.entries()) {
            const match = /^properties\[(.+)\]$/.exec(key);
            if (match) properties[match[1]] = String(value);
          }
          properties['Case personalization'] = input.value.trim();
          const group = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
          properties._personalization_group = group;
          const payload = { items: [
            { id, quantity, properties },
            { id: Number(offer.dataset.serviceId), quantity, properties: {
              'Personalization text': input.value.trim(),
              _personalization_group: group,
              _personalized_variant: String(id)
            } }
          ] };
          if (form.hasAttribute('data-preview-form')) {
            form.dispatchEvent(new CustomEvent('ge:personalization-preview', { bubbles: true, detail: payload }));
            return;
          }
          this.personalizationPending = true;
          error.hidden = true;
          cartLink.hidden = true;
          const buttons = [...this.querySelectorAll('button[name="add"]')];
          buttons.forEach(button => { button.disabled = true; });
          form.setAttribute('aria-busy', 'true');
          try {
            const response = await fetch(offer.dataset.cartUrl, {
              method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              credentials: 'same-origin', body: JSON.stringify(payload)
            });
            const result = await response.json();
            if (!response.ok) throw new Error(typeof result.description === 'string' ? result.description : 'Your items could not be added.');
            window.location.assign(offer.dataset.checkoutUrl);
          } catch (failure) {
            error.textContent = `${failure.message || 'Connection interrupted.'} Please review your cart before trying again; some items may already have been added.`;
            error.hidden = false;
            cartLink.hidden = false;
            // Do not retry ambiguous cart mutations or risk duplicate charges.
            // Customer can reconcile the cart via the visible link.
          } finally {
            form.removeAttribute('aria-busy');
            // On failure keep buying disabled until cart is reviewed / page reloaded.
            // On success keep disabled while checkout navigation completes.
          }
        }, { signal, capture: true });
      }
      disconnectedCallback() {
        this.controller?.abort();
        this.observer?.disconnect();
        this.controller = null;
      }
    });
  }
  if (!customElements.get('ge-reveal')) {
    customElements.define('ge-reveal', class extends HTMLElement {
      connectedCallback() {
        if (this.observer || !('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        this.observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            // Content is visible by default; animation never gates access to it.
            if (typeof entry.target.animate === 'function') entry.target.animate([
              { opacity: 0.25, transform: 'translateY(18px)' },
              { opacity: 1, transform: 'translateY(0)' }
            ], { duration: 620, easing: 'cubic-bezier(.2,.65,.3,1)' });
            this.observer.unobserve(entry.target);
          });
        }, { threshold: 0.08 });
        this.querySelectorAll('[data-ge-reveal]').forEach((element) => this.observer.observe(element));
      }
      disconnectedCallback() { this.observer?.disconnect(); this.observer = null; }
    });
  }
})();
